/**
 * The quest-pack seam (#26 / slice B3).
 *
 * Two endpoints, both admin-only and session-cookie authenticated, both answering
 * `Cache-Control: no-store` — `server/routes/quest-packs.routes.js`. This module is deliberately
 * thin, like `session.js`: it owns no authorization and no selection logic. The server decides
 * what a family may do on every request; this file only refuses to misreport what it answered.
 *
 * Three measured facts about the endpoints shape this file:
 *
 * 1. **`revision` is a STRING, and stays one.** It is a PostgreSQL `bigint`
 *    (`quest-packs.service.js` casts it `::text` in every SELECT and RETURNING), and a JavaScript
 *    number would silently round it past 2^53. Nothing here parses it or increments it; a caller
 *    who hands in a number is refused BEFORE the network, because the server's schema refuses it
 *    anyway (`selectionBodySchema`: a canonical unsigned decimal string).
 * 2. **A 409 carries the CURRENT selection.** Every 409 the service throws captures the stored
 *    row in `details.selection` first (`putSelectionOn`, steps 4-5). That is what a screen must
 *    reconcile to, so it must stay readable — `conflictSelection` is the reader.
 * 3. **Two different 403 shapes are both real.** The SERVICE's carries `details.code`
 *    (`PACKS_FORBIDDEN`); the ROUTER's, from `requireAdmin`, is the auth middleware's flat
 *    `{ error, message }` with no `details` at all. Nothing here reads `details` without checking.
 */

import { ApiError, api } from './client.js';
import { ensureCsrfToken } from './session.js';

export const QUEST_PACKS_PATH = '/api/quest-packs';

/** The PUT path for one pack's selection. The key is encoded, so it cannot escape its segment. */
export function selectionPath(key) {
  return `${QUEST_PACKS_PATH}/${encodeURIComponent(String(key))}/selection`;
}

/** What the server's own schema accepts as a revision: `0`, or digits with no leading zero. */
const CANONICAL_REVISION = /^(0|[1-9][0-9]*)$/;

/**
 * Ask the server for the family's pack catalog with their saved selections.
 *
 * `GET /api/quest-packs` answers `{ success, selectionMode, asOfDate, timezone, packs }`. Each
 * pack is `{ id, title, age, icons: [{ name, alt }], questCount, available, unavailableReason,
 * added, revision }` — note `icons` carry a NAME (`chore_templates.icon_name`), not a `src`.
 *
 * @returns {Promise<Array<object>>} the `packs` array, exactly as the server sent it.
 * @throws {ApiError} 401 with no session, 403 for a non-admin, 503 when the catalog is empty or
 *   the database is away — and a status-200 `ApiError` when a 200 does not carry an array of
 *   packs. A 200 that does not describe the catalog is a FAULT, not an empty catalog: reporting it
 *   as "no packs" would show a family an empty wizard for a page that was never a pack list.
 */
export async function fetchQuestPacks() {
  const payload = await api.get(QUEST_PACKS_PATH);
  const packs =
    payload && typeof payload === 'object' && !Array.isArray(payload) ? payload.packs : undefined;
  if (!Array.isArray(packs)) {
    throw new ApiError('the quest-pack endpoint answered 200 without a list of packs', {
      status: 200,
      path: QUEST_PACKS_PATH,
      body: payload,
    });
  }
  return packs;
}

/**
 * Add or remove one pack for the family.
 *
 * `PUT /api/quest-packs/:key/selection` with exactly `{ added, expectedRevision, mutationId }`.
 * The server answers 200 only after COMMIT, with `{ success, selection, mutationId }`.
 *
 * **The returned selection is the SERVER's answer, not the browser's hope.** A caller must publish
 * what comes back rather than the `added` it sent — the same rule `updateTenant` carries — so a
 * change the server refused or overruled is never shown as saved.
 *
 * Replaying the SAME `mutationId` at the SAME `expectedRevision` is idempotent and returns the
 * same answer. A DIFFERENT `mutationId` at a stale revision is a 409 (`SELECTION_CONFLICT`), and
 * so is adding a pack that is no longer available (`PACK_UNAVAILABLE`). Both carry the CURRENT
 * selection in `details.selection`; read it with `conflictSelection`.
 *
 * @param {string} key the pack's stable key, e.g. `little-helpers`
 * @param {{added: boolean, expectedRevision: string, mutationId: string}} change
 * @returns {Promise<{id: string, added: boolean, revision: string}>} what the server STORED.
 * @throws {ApiError} status 0 before the network for a body the server's schema would refuse; 401
 *   with no session; 403 for a non-admin (either shape); 404 for an unknown pack; 409 for a stale
 *   revision or an unavailable add; and status 200 when a 200 does not describe a selection.
 */
export async function setQuestPackSelection(key, { added, expectedRevision, mutationId } = {}) {
  const path = selectionPath(key);

  // Fail before the network. Each of these is a 400 the family can do nothing about, and sending
  // it would make a caller believe a change was attempted.
  const problems = [];
  if (typeof added !== 'boolean') problems.push('added must be true or false');
  if (typeof expectedRevision !== 'string' || !CANONICAL_REVISION.test(expectedRevision)) {
    problems.push('expectedRevision must be the revision STRING the server sent, unparsed');
  }
  if (typeof mutationId !== 'string' || !mutationId) problems.push('a mutationId is required');
  if (problems.length) {
    throw new ApiError(problems.join('; '), { status: 0, path, body: null });
  }

  await ensureCsrfToken();
  const payload = await api.put(path, { added, expectedRevision, mutationId });

  const selection = payload && typeof payload === 'object' ? payload.selection : null;
  if (
    !selection ||
    typeof selection !== 'object' ||
    Array.isArray(selection) ||
    typeof selection.added !== 'boolean' ||
    typeof selection.revision !== 'string'
  ) {
    // A 200 that does not describe the stored selection is a fault, not a save. Returning the
    // request here would let a screen show a change nobody can prove happened.
    throw new ApiError('the change was sent, but the server did not say what it saved', {
      status: 200,
      path,
      body: payload,
    });
  }
  return selection;
}

/**
 * The CURRENT selection a 409 carries, or `null` for anything that is not that.
 *
 * `null` for a non-`ApiError`, for any status but 409, for a body with no `details` (the router's
 * flat 403), and for a `details.selection` that is not a COMPLETE selection — an object with a
 * boolean `added` and a string `revision`, the same two fields the 200 path demands. A `{}` here
 * would otherwise be published as "not added" (`undefined === true`), a state the server never
 * stated (B3 review F-5). The caller reconciles to a non-null answer and treats `null` as "the
 * server did not say", falling back to the last confirmed state.
 *
 * @param {unknown} error
 * @returns {{id: string, added: boolean, revision: string}|null}
 */
export function conflictSelection(error) {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const details = error.body && typeof error.body === 'object' ? error.body.details : null;
  const selection = details && typeof details === 'object' ? details.selection : null;
  if (!selection || typeof selection !== 'object' || Array.isArray(selection)) return null;
  if (typeof selection.added !== 'boolean' || typeof selection.revision !== 'string') return null;
  return selection;
}

/**
 * The service's `details.code` on a pack error, or `null` when the error carries none — which is
 * what the router's own 403 looks like. Never assumed present.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function packErrorCode(error) {
  if (!(error instanceof ApiError)) return null;
  const details = error.body && typeof error.body === 'object' ? error.body.details : null;
  return details && typeof details.code === 'string' ? details.code : null;
}

/**
 * A fresh id for one logical change.
 *
 * `crypto.randomUUID()` is in every browser this app builds for: Vite 7's default target is
 * `baseline-widely-available` (Chrome/Edge 107+, Firefox 104+, Safari 16+), and the function
 * shipped in Chrome 92, Firefox 95 and Safari 15.4. It needs a secure context, which
 * `https://app.chorestory.com` and `localhost` both are. So this carries no fallback and no
 * dependency; a browser without it gets a plain error rather than a made-up id.
 *
 * @returns {string} a v4 uuid
 */
export function newMutationId() {
  const webCrypto = globalThis.crypto;
  if (!webCrypto || typeof webCrypto.randomUUID !== 'function') {
    throw new Error('this browser cannot generate a change id (crypto.randomUUID is missing)');
  }
  return webCrypto.randomUUID();
}
